import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TransactionSplitModal } from "./transaction-split-modal";
import { AccountSelector } from "./account-selector";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmptyStateSuccess } from "@/components/ui/empty-state";
import { NoValidationIssuesEmptyState } from "@/components/empty-states";

interface ValidationTransaction {
  id: string;
  date: string;
  counterparty: string;
  amount: number;
  debitAccount: string;
  creditAccount: string;
  vatRate: string;
  aiConfidence: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'in_review' | 'validated';
  issues: string[];
  originalSuggestion?: Record<string, unknown>;
  reconciliation?: {
    matched: boolean;
    score: number;
    reasoning?: string;
  };
  rawData?: any;
}

interface ValidationGridProps {
  statusFilter?: 'pending' | 'review' | 'validated' | null;
  onSelectionChange?: (selectedIds: string[]) => void;
}

export const ValidationGrid = ({ statusFilter, onSelectionChange }: ValidationGridProps) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<ValidationTransaction | null>(null);
  const [editingDebitId, setEditingDebitId] = useState<string | null>(null);
  const [editingCreditId, setEditingCreditId] = useState<string | null>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(0);
  const [showCreateRuleFor, setShowCreateRuleFor] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch transactions that need validation
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ['validation-transactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('needs_review', true)
        .order('transaction_date', { ascending: false });

      if (error) throw error;

      return (data || []).map(t => {
        // Safely parse ai_classification
        const classification = t.ai_classification as Record<string, unknown> | null;
        const confidence = t.ai_classification_confidence || 0;
        
        // Get account from DB field (where AI stored it) or from classification suggestion
        const accountNumber = t.account_number || (classification?.accountNumber as string) || '';
        const accountName = classification?.accountName as string || '';
        const debitAccount = accountNumber ? `${accountNumber} - ${accountName}` : '';
        
        // Extract reconciliation from ai_classification if available
        const aiClassificationData = t.ai_classification as Record<string, any> | null;
        const reconciliation = aiClassificationData?.reconciliation;
        
        return {
          id: t.id,
          date: new Date(t.transaction_date).toLocaleDateString('de-CH'),
          counterparty: t.counterparty || 'Unknown',
          amount: t.amount,
          debitAccount,
          creditAccount: '1020 - PostFinance', // Credit is always the bank account for expenses
          vatRate: (classification?.vatRate as number) ? `${classification.vatRate}%` : '0%',
          aiConfidence: confidence,
          priority: confidence < 50 ? 'critical' as const : 
                   confidence < 70 ? 'high' as const : 
                   confidence < 85 ? 'medium' as const : 
                   'low' as const,
          status: t.human_verified ? 'validated' as const : 'pending' as const,
          issues: [],
          originalSuggestion: classification || undefined,
          reconciliation: reconciliation || undefined,
          rawData: t
        };
      }) as ValidationTransaction[];
    }
  });

  // Apply status filter
  const filteredTransactions = transactions.filter(t => {
    if (!statusFilter) return true;
    if (statusFilter === 'pending') return t.status === 'pending';
    if (statusFilter === 'review') return t.status === 'in_review';
    if (statusFilter === 'validated') return t.status === 'validated';
    return true;
  });

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const selectedId = Array.from(selectedRows)[0];
      const currentTransaction = selectedId 
        ? filteredTransactions.find(t => t.id === selectedId)
        : filteredTransactions[focusedRowIndex];

      if (!currentTransaction) return;

      switch (e.key.toLowerCase()) {
        case 'a':
          if (currentTransaction) {
            handleApprove(currentTransaction.id);
            toast.success('Shortcut: Approved (A)');
          }
          break;
        case 'r':
          if (currentTransaction && confirm('Reject this transaction? (R)')) {
            handleReject(currentTransaction.id);
          }
          break;
        case 'e':
          if (currentTransaction) {
            setEditingDebitId(currentTransaction.id);
            toast.info('Shortcut: Edit mode (E)');
          }
          break;
        case 's':
          if (currentTransaction) {
            openSplitModal(currentTransaction);
            toast.info('Shortcut: Split transaction (S)');
          }
          break;
        case 'j':
          // Navigate down
          setFocusedRowIndex(prev => Math.min(prev + 1, filteredTransactions.length - 1));
          e.preventDefault();
          break;
        case 'k':
          // Navigate up
          setFocusedRowIndex(prev => Math.max(prev - 1, 0));
          e.preventDefault();
          break;
        case ' ':
          // Select/deselect current row
          if (currentTransaction) {
            handleRowSelect(currentTransaction.id, !selectedRows.has(currentTransaction.id));
          }
          e.preventDefault();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedRows, filteredTransactions, focusedRowIndex]);

  // Mutation to approve transaction
  const approveMutation = useMutation({
    mutationFn: async (transactionId: string) => {
      const { error } = await supabase
        .from('transactions')
        .update({
          needs_review: false,
          human_verified: true
        })
        .eq('id', transactionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      toast.success('Transaction approved');
    },
    onError: (error) => {
      toast.error(`Failed to approve: ${error.message}`);
    }
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async (transactionIds: string[]) => {
      const { error } = await supabase
        .from('transactions')
        .update({
          needs_review: false,
          human_verified: true
        })
        .in('id', transactionIds);

      if (error) throw error;
      return transactionIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      setSelectedRows(new Set());
      toast.success(`${count} transactions approved`);
    },
    onError: (error) => {
      toast.error(`Bulk approve failed: ${error.message}`);
    }
  });

  // Mutation to reject transaction
  const rejectMutation = useMutation({
    mutationFn: async (transactionId: string) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      toast.success('Transaction rejected');
    },
    onError: (error) => {
      toast.error(`Failed to reject: ${error.message}`);
    }
  });

  // Bulk reject mutation
  const bulkRejectMutation = useMutation({
    mutationFn: async (transactionIds: string[]) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .in('id', transactionIds);

      if (error) throw error;
      return transactionIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      setSelectedRows(new Set());
      toast.success(`${count} transactions rejected`);
    },
    onError: (error) => {
      toast.error(`Bulk reject failed: ${error.message}`);
    }
  });

  // Mutation to update account
  const updateAccountMutation = useMutation({
    mutationFn: async ({ transactionId, accountNumber }: { transactionId: string; accountNumber: string }) => {
      const { error } = await supabase
        .from('transactions')
        .update({
          account_number: accountNumber,
          needs_review: true,
          human_verified: false
        })
        .eq('id', transactionId);

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      toast.success('Account updated');
      // Show "Create Rule" button
      setShowCreateRuleFor(variables.transactionId);
      setTimeout(() => setShowCreateRuleFor(null), 10000);
    },
    onError: (error) => {
      toast.error(`Failed to update account: ${error.message}`);
    }
  });

  // Mutation to create rule from correction
  const createRuleMutation = useMutation({
    mutationFn: async ({ 
      transactionId, 
      counterparty, 
      accountNumber, 
      vatRate,
      organizationId 
    }: { 
      transactionId: string; 
      counterparty: string; 
      accountNumber: string; 
      vatRate: number;
      organizationId: string;
    }) => {
      const { error } = await supabase
        .from('transaction_patterns')
        .insert({
          counterparty: counterparty.toLowerCase(),
          description_pattern: counterparty.toLowerCase(),
          account: accountNumber,
          vat_rate: vatRate,
          confidence: 95,
          learned_from: transactionId,
          organization_id: organizationId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Rule created from correction! This will apply to future similar transactions.');
    },
    onError: (error) => {
      toast.error(`Failed to create rule: ${error.message}`);
    }
  });

  const handleSelectAll = (checked: boolean) => {
    const newSelection = checked ? new Set(filteredTransactions.map(t => t.id)) : new Set<string>();
    setSelectedRows(newSelection);
    onSelectionChange?.(Array.from(newSelection));
  };

  const handleApprove = (transactionId: string) => {
    approveMutation.mutate(transactionId);
  };

  const handleReject = (transactionId: string) => {
    if (confirm('Are you sure you want to reject this transaction?')) {
      rejectMutation.mutate(transactionId);
    }
  };

  const handleEdit = (transaction: ValidationTransaction) => {
    setEditingDebitId(transaction.id);
  };

  const handleCreateRule = async (transaction: ValidationTransaction, correctedAccount: string) => {
    // Extract VAT rate from transaction
    const vatRate = parseFloat(transaction.vatRate.replace('%', '')) || 8.1;
    
    // Get organization_id from the raw transaction data
    const organizationId = transaction.rawData?.organization_id;
    
    if (!organizationId) {
      toast.error('Organization ID not found');
      return;
    }

    createRuleMutation.mutate({
      transactionId: transaction.id,
      counterparty: transaction.counterparty,
      accountNumber: correctedAccount,
      vatRate,
      organizationId,
    });
  };

  const handleRowSelect = (transactionId: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(transactionId);
    } else {
      newSelected.delete(transactionId);
    }
    setSelectedRows(newSelected);
    onSelectionChange?.(Array.from(newSelected));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'border-l-validation-critical';
      case 'high': return 'border-l-validation-high';
      case 'medium': return 'border-l-validation-medium';
      case 'low': return 'border-l-validation-low';
      default: return 'border-l-gray-300';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'bg-validation-low';
    if (confidence >= 75) return 'bg-validation-medium';
    if (confidence >= 60) return 'bg-validation-high';
    return 'bg-validation-critical';
  };

  const openSplitModal = (transaction: ValidationTransaction) => {
    setSelectedTransaction(transaction);
    setSplitModalOpen(true);
  };

  if (isLoading) {
    return (
      <section className="flex items-center justify-center py-12">
        <div className="text-brand-text-secondary">Loading transactions...</div>
      </section>
    );
  }

  if (filteredTransactions.length === 0 && !isLoading) {
    // Show professional success state when all validated
    if (transactions.length === 0) {
      return <NoValidationIssuesEmptyState />;
    }
    
    // Show filter mismatch state
    return (
      <EmptyStateSuccess
        title="No Matches"
        description="No transactions match the selected filter. Try adjusting your criteria."
        variant="compact"
      />
    );
  }

  return (
    <>
      <section className="space-y-2">
        {/* Bulk Action Bar */}
        {selectedRows.size > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center justify-between animate-in slide-in-from-top mb-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                <i className="fa-solid fa-check-square mr-2 text-blue-600" />
                {selectedRows.size} transaction{selectedRows.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedRows(new Set())}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const ids = Array.from(selectedRows);
                  if (confirm(`Approve ${ids.length} transactions?`)) {
                    bulkApproveMutation.mutate(ids);
                  }
                }}
                disabled={bulkApproveMutation.isPending}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              >
                <i className="fa-solid fa-check" />
                Approve All
              </button>
              <button
                onClick={() => {
                  const ids = Array.from(selectedRows);
                  if (confirm(`Reject and delete ${ids.length} transactions? This cannot be undone.`)) {
                    bulkRejectMutation.mutate(ids);
                  }
                }}
                disabled={bulkRejectMutation.isPending}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              >
                <i className="fa-solid fa-times" />
                Reject All
              </button>
              <button
                onClick={() => {
                  toast.info('Export feature coming soon!');
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              >
                <i className="fa-solid fa-download" />
                Export
              </button>
            </div>
          </div>
        )}

        {/* Grid Header */}
        <div className="px-4 py-2 bg-brand-secondary rounded-lg flex items-center gap-4 text-xs font-medium text-brand-text-secondary">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
          <div className="w-[8%] cursor-pointer hover:text-brand-primary">Date</div>
          <div className="w-[16%] cursor-pointer hover:text-brand-primary">Counterparty</div>
          <div className="w-[12%] cursor-pointer hover:text-brand-primary">Amount</div>
          <div className="w-[15%] cursor-pointer hover:text-brand-primary">Debit Account</div>
          <div className="w-[15%] cursor-pointer hover:text-brand-primary">Credit Account</div>
          <div className="w-[8%] cursor-pointer hover:text-brand-primary">VAT</div>
          <div className="w-[10%] cursor-pointer hover:text-brand-primary">Issues</div>
          <div className="w-[8%] cursor-pointer hover:text-brand-primary">Confidence</div>
          <div className="w-[8%]">Actions</div>
        </div>

        {/* Validation Rows */}
        {filteredTransactions.map((transaction) => (
          <div 
            key={transaction.id}
            className={`bg-white p-4 rounded-xl border border-transparent hover:border-brand-accent shadow-sm flex items-center gap-4 text-sm transition-all duration-200 border-l-4 ${getPriorityColor(transaction.priority)}`}
          >
            <input 
              type="checkbox" 
              className="h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
              checked={selectedRows.has(transaction.id)}
              onChange={(e) => handleRowSelect(transaction.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="w-[8%] text-xs">{transaction.date}</div>
            <div className="w-[16%]">{transaction.counterparty}</div>
            <div className={`w-[12%] font-medium ${transaction.amount < 0 ? 'text-brand-danger' : 'text-brand-success'}`}>
              <div className="flex items-center gap-2">
                <span>
                  {transaction.amount < 0 ? '-' : '+'} CHF {Math.abs(transaction.amount).toFixed(2)}
                </span>
                {transaction.reconciliation?.matched && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const recon = transaction.reconciliation;
                      
                      let details = `🎯 Reconciliation Details\n\n`;
                      details += `Score: ${recon.score}%\n`;
                      details += `Match: ${recon.matched ? 'Yes' : 'No'}\n\n`;
                      
                      if (recon.reasoning) {
                        details += `💡 Reasoning:\n${recon.reasoning}\n\n`;
                      }
                      
                      alert(details);
                    }}
                    className={`text-xs px-2 py-0.5 rounded cursor-pointer transition-all hover:scale-105 ${
                      transaction.reconciliation.score >= 90 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : transaction.reconciliation.score >= 70
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                    title="Click for reconciliation details"
                  >
                    ✓ {transaction.reconciliation.score}%
                  </button>
                )}
              </div>
            </div>
            <div className="w-[15%]" onClick={(e) => e.stopPropagation()}>
              {editingDebitId === transaction.id ? (
                <div className="flex items-center gap-1">
                  <AccountSelector 
                    value={transaction.debitAccount}
                    onChange={(account) => {
                      updateAccountMutation.mutate({
                        transactionId: transaction.id,
                        accountNumber: account.split(' - ')[0]
                      });
                      setEditingDebitId(null);
                    }}
                    placeholder="Select account"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingDebitId(null);
                    }}
                    className="p-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  {transaction.debitAccount ? (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium" title="AI Vorschlag">
                      {transaction.debitAccount}
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-validation-critical-bg text-validation-critical rounded text-xs font-medium">
                      Konto wählen
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingDebitId(transaction.id);
                    }}
                    className="px-2 py-1 bg-brand-accent text-white rounded text-xs hover:bg-brand-primary"
                    title="Correct"
                  >
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  {showCreateRuleFor === transaction.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateRule(transaction, transaction.debitAccount.split(' - ')[0]);
                        setShowCreateRuleFor(null);
                      }}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1 animate-pulse"
                      title="Create rule from this correction"
                    >
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      <span>→ Rule</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="w-[15%]" onClick={(e) => e.stopPropagation()}>
              {editingCreditId === transaction.id ? (
                <div className="flex items-center gap-1">
                  <AccountSelector 
                    value={transaction.creditAccount}
                    onChange={(account) => {
                      // Update credit account if needed
                      toast.info('Credit account updated');
                      setEditingCreditId(null);
                    }}
                    placeholder="Select credit account"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCreditId(null);
                    }}
                    className="p-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">{transaction.creditAccount}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCreditId(transaction.id);
                    }}
                    className="px-2 py-1 bg-brand-secondary text-brand-primary rounded text-xs hover:bg-brand-accent"
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen"></i>
                  </button>
                </div>
              )}
            </div>
            <div className="w-[8%]">
              <span className="px-2 py-1 text-xs font-medium rounded-md bg-vat-standard text-vat-standard-text">
                {transaction.vatRate}
              </span>
            </div>
            <div className="w-[10%]">
              <div className="flex flex-wrap gap-1">
                {transaction.issues.map((issue, index) => (
                  <span key={index} className="px-1 py-0.5 bg-validation-critical-bg text-validation-critical text-xs rounded">
                    {issue}
                  </span>
                ))}
              </div>
            </div>
            <div className="w-[8%] flex items-center gap-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full ${getConfidenceColor(transaction.aiConfidence)}`}
                  style={{ width: `${transaction.aiConfidence}%` }}
                ></div>
              </div>
              <span className="text-xs font-bold">{transaction.aiConfidence}%</span>
            </div>
            <div className="w-[8%] flex items-center gap-1">
              <button
                className="p-1 bg-brand-success-bg text-brand-success rounded text-xs hover:bg-brand-success hover:text-white transition-colors cursor-pointer"
                title="Approve"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(transaction.id);
                }}
                disabled={approveMutation.isPending}
              >
                <i className="fa-solid fa-check"></i>
              </button>
              <button
                className="p-1 bg-brand-secondary text-brand-primary rounded text-xs hover:bg-brand-accent transition-colors cursor-pointer"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(transaction);
                }}
              >
                <i className="fa-solid fa-edit"></i>
              </button>
              <button
                className="p-1 bg-brand-warning-bg text-brand-warning rounded text-xs hover:bg-brand-warning hover:text-white transition-colors cursor-pointer"
                title="Split Transaction"
                onClick={(e) => {
                  e.stopPropagation();
                  openSplitModal(transaction);
                }}
              >
                <i className="fa-solid fa-scissors"></i>
              </button>
              <button
                className="p-1 bg-brand-danger-bg text-brand-danger rounded text-xs hover:bg-brand-danger hover:text-white transition-colors cursor-pointer"
                title="Reject"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReject(transaction.id);
                }}
                disabled={rejectMutation.isPending}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
          </div>
        ))}
      </section>

      {splitModalOpen && selectedTransaction && (
        <TransactionSplitModal
          transaction={selectedTransaction}
          isOpen={splitModalOpen}
          onClose={() => setSplitModalOpen(false)}
        />
      )}
    </>
  );
};